import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import QorumCouncilOrb from "@/components/QorumCouncilOrb";
import { CouncilBubble } from "@/components/qorum/CouncilBubble";
import { SearchLiveFeed } from "@/components/qorum/SearchLiveFeed";
import { ConnectionPill } from "@/components/qorum/ConnectionPill";
import { GreffierBriefingCard } from "@/components/qorum/GreffierBriefingCard";
import { DeliberationErrorBoundary } from "@/components/qorum/DeliberationErrorBoundary";
import { useDeliberation, TURN_ORDER } from "@/hooks/use-deliberation";
import { useResearchSources } from "@/hooks/use-research-sources";
import { CockpitDeliberation } from "@/components/qv3/CockpitDeliberation";
import { CockpitVerif } from "@/components/qv3/CockpitVerif";
import InvestigationLive from "@/components/Deliberation/InvestigationLive";
import { useThrottledBubbles } from "@/hooks/use-throttled-bubbles";
import { COUNCIL } from "@/lib/council";
import { LLM_MODELS, type LLMPersonaId } from "@/components/brand/llm-logos";
import { toast } from "sonner";
import { modeToLabel } from "@/lib/verdict-format";
import { apiFetch } from "@/lib/api";
import { socialMeta } from "@/lib/seo-meta";

type DelibSearch = { branch?: "business" | "tendances" | "audit" | "verif" };
export const Route = createFileRoute("/deliberation/$id")({
  head: ({ params }) => ({
    meta: [{ title: "Le Conseil · Hōjin" }, { name: "robots", content: "noindex, nofollow" },
      ...socialMeta({
        title: "Le Conseil · Hōjin",
        description: "Le Conseil · Hōjin",
        url: `https://hojin.ai/deliberation/${params.id}`,
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: `https://hojin.ai/deliberation/${params.id}` }],
  }),
  validateSearch: (s: Record<string, unknown>): DelibSearch => {
    const b = s?.branch;
    return b === "business" || b === "tendances" || b === "audit" || b === "verif"
      ? { branch: b }
      : {};
  },
  component: DeliberationPage,
});

const COUNCIL_BY_ID = Object.fromEntries(COUNCIL.map((c) => [c.id, c]));

const ACTES = [
  { key: "tour", label: "I · TOUR" },
  { key: "contre", label: "II · CONTRE-PIED" },
  { key: "synthese", label: "III · SYNTHÈSE" },
  { key: "dissensus", label: "IV · DISSENSUS" },
  { key: "verdict", label: "V · VERDICT" },
  { key: "sceau", label: "VI · SCEAU" },
];

export function DeliberationPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch() as DelibSearch;
  return (
    <ProtectedRoute>
      <DeliberationErrorBoundary id={id}>
        <LaTable key={id} id={id} branchHint={search.branch ?? null} />
      </DeliberationErrorBoundary>
    </ProtectedRoute>
  );
}


function LaTable({ id, branchHint }: { id: string; branchHint: DelibSearch["branch"] | null }) {
  const state = useDeliberation(id);
  const navigate = useNavigate();
  // Branche effective : hint search-param (instantané) → écrasé dès que le
  // backend renvoie la valeur réelle. Évite le flash du cockpit classique
  // avant la bascule vers CockpitVerif (sprint 24/06/2026).
  const effectiveBranch = state.branch ?? branchHint ?? null;

  // IMPORTANT : tous les hooks DOIVENT être appelés inconditionnellement,
  // avant tout early return — sinon le nombre de hooks change entre deux
  // renders (ex. quand state.branch passe à "verif" ou que streamStalled
  // bascule), ce qui déclenche React error #300.
  const groupedSources = useResearchSources(
    state.investigationSources,
    state.investigationProgress,
  );

  // P0-quater — RELANCER une séance échouée : crée une délibération NEUVE (route /relance) et navigue vers
  // elle. Un état terminal n'offre qu'une sortie qui ABOUTIT — surtout PAS un « Réessayer » qui rouvrirait
  // le /stream idempotent et rejouerait l'échec à l'infini.
  const [relancing, setRelancing] = useState(false);
  const relancerSeance = async () => {
    if (relancing) return;
    setRelancing(true);
    try {
      const r = await apiFetch<{ conversation_id?: string }>(`/api/chat/${id}/relance`, { method: "POST" });
      const child = r?.conversation_id;
      if (child) {
        navigate({ to: "/deliberation/$id", params: { id: child } });
        return; // on laisse `relancing` à true : la navigation démonte l'écran
      }
      toast.error("La relance n'a pas pu démarrer — réessayez depuis vos saisines.");
      setRelancing(false);
    } catch {
      toast.error("La relance n'a pas pu démarrer — réessayez depuis vos saisines.");
      setRelancing(false);
    }
  };

  // Échec TERMINAL (status DB `failed` OU event SSE `error severity:fatal`) :
  // la séance a été interrompue et aucun verdict ne viendra. On AVOUE l'échec
  // (doctrine : un échec s'affiche, jamais un reset muet / timer dans le vide),
  // avec la raison backend et une relance possible. Prioritaire sur le stall
  // « prend plus de temps » (qui, lui, suppose une séance encore en cours).
  if (state.streamFailed && !state.isCompleted) {
    return (
      <div
        data-testid="deliberation-failed"
        style={{
          minHeight: "calc(100vh - 24px)",
          display: "grid",
          placeItems: "center",
          padding: 32,
          background: "#000000",
          color: "#FFFFFF",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 560 }}>
          <div
            style={{
              fontFamily: '"JetBrains Mono", "DM Mono", ui-monospace, monospace',
              fontSize: 10,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: "#FF1744",
              marginBottom: 18,
            }}
          >
            Séance interrompue
          </div>
          <p
            style={{
              fontFamily: '"Fraunces", "Playfair Display", Georgia, serif',
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: 22,
              lineHeight: 1.4,
              color: "#FFFFFF",
              margin: "0 0 28px",
            }}
          >
            {state.streamFailReason ??
              "La séance a été interrompue avant son terme — aucun verdict n'a été rendu. Vous pouvez la relancer."}
          </p>
          <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <button
              type="button"
              onClick={relancerSeance}
              disabled={relancing}
              style={{
                background: relancing ? "#555555" : "#FFFFFF",
                border: `1px solid ${relancing ? "#555555" : "#FFFFFF"}`,
                color: relancing ? "#CCCCCC" : "#000000",
                padding: "12px 20px",
                fontFamily: '"JetBrains Mono", "DM Mono", ui-monospace, monospace',
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                cursor: relancing ? "wait" : "pointer",
              }}
            >
              {relancing ? "Relance en cours…" : "Relancer la séance"}
            </button>
            <button
              type="button"
              onClick={() => navigate({ to: "/archives" })}
              style={{
                background: "transparent",
                border: "1px solid #FF1744",
                color: "#FF1744",
                padding: "12px 20px",
                fontFamily: '"JetBrains Mono", "DM Mono", ui-monospace, monospace',
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Voir mes saisines
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Stall (timeout 120s sans complete_conversation_done OU fatal_error explicite) :
  // afficher un message clair + lien vers Archives plutôt qu'Erreur 500.
  if (state.streamStalled && !state.isCompleted) {
    return (
      <div
        data-testid="deliberation-stalled"
        style={{
          minHeight: "calc(100vh - 24px)",
          display: "grid",
          placeItems: "center",
          padding: 32,
          background: "#000000",
          color: "#FFFFFF",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 560 }}>
          <div
            style={{
              fontFamily: '"JetBrains Mono", "DM Mono", ui-monospace, monospace',
              fontSize: 10,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: "#FF1744",
              marginBottom: 18,
            }}
          >
            Le Conseil délibère encore
          </div>
          <p
            style={{
              fontFamily: '"Fraunces", "Playfair Display", Georgia, serif',
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: 22,
              lineHeight: 1.4,
              color: "#FFFFFF",
              margin: "0 0 28px",
            }}
          >
            Le Conseil prend plus de temps que prévu. Vous pouvez continuer
            d'attendre, ou retrouver votre saisine dans les Archives.
          </p>
          <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
            <button
              type="button"
              onClick={() => state.reconnect()}
              style={{
                background: "#FFFFFF",
                border: "1px solid #FFFFFF",
                color: "#000000",
                padding: "12px 20px",
                fontFamily: '"JetBrains Mono", "DM Mono", ui-monospace, monospace',
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Continuer d'attendre
            </button>
            <button
              type="button"
              onClick={() => navigate({ to: "/archives" })}
              style={{
                background: "transparent",
                border: "1px solid #FF1744",
                color: "#FF1744",
                padding: "12px 20px",
                fontFamily: '"JetBrains Mono", "DM Mono", ui-monospace, monospace',
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Voir mes saisines
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== Branche Vérif — vue dédiée (pas de bulles, fil d'activité) =====
  // Pas de bascule auto vers les observations : CockpitVerif affiche un
  // bouton « Voir les observations du Conseil » dès que clarityReady,
  // pour marquer la fin de l'examen par un geste explicite du user.
  if (effectiveBranch === "verif") {
    return <CockpitVerif state={state} />;
  }

  // Si la branche est encore inconnue (pas de hint search-param ET pas
  // encore reçue du backend), skeleton neutre — évite d'afficher la mauvaise
  // UI le temps du fetch /history.
  if (effectiveBranch === null) {
    return (
      <div
        data-testid="deliberation-branch-loading"
        style={{
          minHeight: "calc(100vh - 24px)",
          display: "grid",
          placeItems: "center",
          background: "var(--ivory)",
        }}
      >
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 10,
            letterSpacing: 2.5,
            textTransform: "uppercase",
            color: "var(--gold-deep)",
          }}
        >
          Convocation du Conseil…
        </div>
      </div>
    );
  }

  // ===== Mode LIVE — nouveau cockpit v3.1 noir/blanc/or =====
  return <CockpitDeliberation state={state} sources={groupedSources} />;
}


function VerdictReadyBadge({
  verdictId,
  meetingDone,
  isNarrow,
}: {
  verdictId: string;
  meetingDone: boolean;
  isNarrow: boolean;
}) {
  const navigate = useNavigate();
  return (
    <div
      data-testid="verdict-ready-badge"
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        top: 36,
        left: "50%",
        transform: "translateX(-50%)",
        width: isNarrow ? "calc(100% - 24px)" : "min(640px, calc(100% - 48px))",
        zIndex: 60,
        display: "flex",
        flexDirection: isNarrow ? "column" : "row",
        alignItems: isNarrow ? "stretch" : "center",
        gap: 14,
        padding: "14px 18px",
        background: "#FFFCF4",
        border: "1px solid #00E5FF",
        borderRadius: 12,
        boxShadow: "0 8px 24px -10px rgba(74,31,45,0.18)",
        animation: "qorum-fade-in 280ms ease-out",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "rgba(45,134,89,0.12)",
          color: "#2d8659",
          display: "grid",
          placeItems: "center",
          fontSize: 16,
          fontWeight: 700,
          alignSelf: isNarrow ? "flex-start" : "center",
        }}
      >
        ✓
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h4
          style={{
            fontFamily: '"Fraunces", "Playfair Display", Georgia, serif',
            fontSize: 16,
            fontWeight: 600,
            color: "#FF1744",
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          Verdict prêt
        </h4>
        <p
          style={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: 13,
            lineHeight: 1.45,
            color: "rgba(45,38,32,0.72)",
            margin: "4px 0 0 0",
          }}
        >
          {meetingDone
            ? "Réunion terminée — redirection vers le verdict dans un instant…"
            : "Le débat continue. Vous pouvez attendre la fin ou consulter le verdict dès maintenant."}
        </p>
      </div>
      <button
        type="button"
        onClick={() => navigate({ to: "/verdict/$id", params: { id: verdictId } })}
        style={{
          flexShrink: 0,
          width: isNarrow ? "100%" : "auto",
          background: "transparent",
          border: "0.5px solid #FF1744",
          color: "#FF1744",
          padding: "10px 18px",
          borderRadius: 8,
          fontFamily: '"DM Mono", monospace',
          fontSize: 11,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          cursor: "pointer",
          transition: "background 160ms, color 160ms",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#FF1744";
          e.currentTarget.style.color = "#FFFCF4";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = "#FF1744";
        }}
      >
        Voir le verdict maintenant →
      </button>
    </div>
  );
}

/* ─────────────────────── LEFT RAIL (logo + orb + caption) ─────────────────────── */

function LeftRail({
  state,
  activeName,
  archiveMode: _archiveMode = false,
}: {
  state: ReturnType<typeof useDeliberation>;
  activeName: string;
  archiveMode?: boolean;
}) {
  const navigate = useNavigate();
  return (
    <aside
      aria-label="Briefing du greffe et recherches"
      data-rp="sidebar-mobile-collapse"
      style={{
        background: "var(--ivory)",
        borderRight: "0.5px solid var(--stone-mid)",
        padding: "22px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 20,
        overflowY: "auto",
      }}
    >
      <button
        onClick={() => navigate({ to: "/foyer" })}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          alignSelf: "flex-start",
        }}
        aria-label="Retour au Bureau"
      >
        <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 0 }}>
          <span
            style={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 900,
              fontSize: 26,
              color: "var(--navy)",
              fontVariationSettings: "'opsz' 144, 'SOFT' 30",
              letterSpacing: -1.5,
              lineHeight: 1,
            }}
          >
            Hōjin
          </span>
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              background: "var(--terracotta)",
              marginLeft: 3,
              marginBottom: 3,
            }}
          />
        </span>
      </button>

      <div style={{ width: "100%", borderTop: "0.5px solid var(--stone-mid)" }} />

      <SearchLiveFeed searches={state.searches} />


      <div>
        <Label>La Saisine</Label>
        <h1
          style={{
            fontFamily: '"Playfair Display", serif',
            fontStyle: "italic",
            fontWeight: 700,
            fontSize: 20,
            lineHeight: 1.15,
            color: "var(--charcoal)",
            margin: "8px 0 14px",
            letterSpacing: -0.5,
          }}
        >
          {state.question}
        </h1>
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 9,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: "var(--gold-deep)",
            marginBottom: 6,
          }}
        >
          N° {state.saisineNumber} &nbsp; Mode · {modeToLabel(state.mode)}
        </div>
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 9,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: "var(--smoke)",
          }}
        >
          {state.totalTurns} prises de parole attendues
        </div>
      </div>

      <GreffierBriefingCard briefing={state.greffierBriefing} />

      <div>
        <Label>Sièges convoqués</Label>
        <p
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 12.5,
            lineHeight: 1.55,
            color: "var(--smoke)",
            margin: "8px 0 0",
          }}
        >
          Les sept conseillers permanents du Conseil siègent pour cette saisine.
        </p>
      </div>

      <div style={{ marginTop: "auto", borderTop: "0.5px solid var(--stone-mid)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
        <ConnectionPill state={state.connectionState} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "var(--navy)",
              color: "var(--ivory)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            P
          </div>
          <div>
            <div
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: 9,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: "var(--charcoal)",
                fontWeight: 600,
              }}
            >
              Le Président
            </div>
            <div
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: 8,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                color: "var(--smoke)",
              }}
            >
              Vol. I · MMXXVI
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}


/* ─────────────────────── SIDEBAR (left, 300px) ─────────────────────── */

function Sidebar({ state }: { state: ReturnType<typeof useDeliberation> }) {
  const navigate = useNavigate();
  return (
    <aside
      style={{
        background: "var(--ivory)",
        borderRight: "0.5px solid var(--stone-mid)",
        padding: "26px 26px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 28,
        overflowY: "auto",
      }}
    >
      <button
        onClick={() => navigate({ to: "/foyer" })}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}
        aria-label="Retour au Bureau"
      >
        <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 0 }}>
          <span
            style={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 900,
              fontSize: 30,
              color: "var(--navy)",
              fontVariationSettings: "'opsz' 144, 'SOFT' 30",
              letterSpacing: -1.5,
              lineHeight: 1,
            }}
          >
            Hōjin
          </span>
          <span
            aria-hidden
            style={{ width: 7, height: 7, background: "var(--terracotta)", marginLeft: 3, marginBottom: 4 }}
          />
        </span>
      </button>

      <div style={{ borderTop: "0.5px solid var(--stone-mid)" }} />

      <div>
        <Label>La Saisine</Label>
        <h1
          style={{
            fontFamily: '"Playfair Display", serif',
            fontStyle: "italic",
            fontWeight: 700,
            fontSize: 26,
            lineHeight: 1.15,
            color: "var(--charcoal)",
            margin: "10px 0 18px",
            letterSpacing: -0.5,
          }}
        >
          {state.question}
        </h1>
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 10,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: "var(--gold-deep)",
            marginBottom: 6,
          }}
        >
          N° {state.saisineNumber} &nbsp; Mode · {modeToLabel(state.mode)}
        </div>
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 10,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: "var(--smoke)",
          }}
        >
          {state.totalTurns} prises de parole attendues
        </div>
      </div>

      <div
        style={{
          borderLeft: "2px solid var(--terracotta)",
          background: "var(--cream)",
          padding: "14px 16px",
        }}
      >
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 10,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: "var(--leather)",
            marginBottom: 8,
            fontWeight: 600,
          }}
        >
          Briefing du greffe
        </div>
        <p
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: "italic",
            fontSize: 14.5,
            lineHeight: 1.5,
            color: "var(--charcoal)",
            margin: 0,
          }}
        >
          Hōjin a précisé le cadre&nbsp;: décision à 60 jours, signaux marché documentés,
          runway préservé. Le Conseil délibère sur cette base.
        </p>
      </div>

      <div>
        <Label>Sièges convoqués</Label>
        <p
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 13.5,
            lineHeight: 1.55,
            color: "var(--smoke)",
            margin: "10px 0 0",
          }}
        >
          Les sept conseillers permanents du Conseil siègent pour cette saisine.
        </p>
      </div>

      <div style={{ marginTop: "auto", borderTop: "0.5px solid var(--stone-mid)", paddingTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "var(--navy)",
              color: "var(--ivory)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            P
          </div>
          <div>
            <div
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: 10,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: "var(--charcoal)",
                fontWeight: 600,
              }}
            >
              Le Président
            </div>
            <div
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: 9,
                letterSpacing: 1.5,
                textTransform: "uppercase",
                color: "var(--smoke)",
              }}
            >
              Vol. I · MMXXVI
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontFamily: '"DM Mono", monospace',
        fontSize: 10,
        letterSpacing: 3,
        textTransform: "uppercase",
        color: "var(--smoke)",
      }}
    >
      {children}
    </div>
  );
}

/* ─────────────────────── STAGE (center) ─────────────────────── */

function Stage({
  state,
  activeName,
  streamRef,
  bottomRef,
  messages,
  hideOrb = false,
  archiveMode: _archiveMode = false,
}: {
  state: ReturnType<typeof useDeliberation>;
  activeName: string;
  streamRef: React.RefObject<HTMLDivElement | null>;
  bottomRef: React.RefObject<HTMLDivElement | null>;
  messages: ReturnType<typeof useDeliberation>["messages"];
  hideOrb?: boolean;
  archiveMode?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const submit = () => {
    if (!draft.trim()) return;
    toast.success("Message transmis au Conseil — pris en compte au prochain tour");
    setDraft("");
  };

  const actLabel =
    state.currentPhase === "summon"
      ? "I — CONVOCATION"
      : state.currentPhase === "debate"
      ? "I — TOUR DE TABLE"
      : state.currentPhase === "converge"
      ? "II — CONVERGENCE"
      : state.currentPhase === "verdict"
      ? "V — VERDICT"
      : "I — TOUR DE TABLE";

  const stageBusy =
    state.currentPhase === "summon" || state.currentPhase === "converge";

  // Indicator "X écrit…" : prochain conseiller dont la bulle est en queue.
  const displayedIds = new Set(messages.map((m) => m.id));
  const nextPending = state.displayedMessages.find((m) => !displayedIds.has(m.id));
  const nextPendingName = nextPending
    ? COUNCIL_BY_ID[nextPending.persona]?.name ?? ""
    : "";
  const showTyping = !!nextPending && messages.length > 0;

  return (
    <section
      aria-label="Délibération du Conseil"
      aria-busy={stageBusy}
      data-rp="stage-mobile"
      style={{
        position: "relative",
        background: "var(--ivory)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Top thin bar */}
      <div
        style={{
          padding: "14px 32px",
          borderBottom: "0.5px solid var(--stone-mid)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: '"DM Mono", monospace',
          fontSize: 10,
          letterSpacing: 3,
          textTransform: "uppercase",
          color: "var(--smoke)",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <span>Acte</span>
          <ActDots phase={state.currentPhase} />
          <span style={{ color: "var(--charcoal)" }}>{actLabel}</span>
        </div>
        <span style={{ color: "var(--gold-deep)" }}>Le Conseil siège</span>
      </div>

      {/* Orb stage (paper) — compact, hidden when LeftRail visible */}
      <div
        role="img"
        aria-label={`Orbe du Conseil — phase ${state.currentPhase}`}
        style={{
          display: hideOrb ? "none" : "flex",
          background: "var(--paper)",
          height: 180,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          position: "relative",
          borderBottom: "0.5px solid var(--stone-mid)",
          overflow: "hidden",
        }}
      >
        <div style={{ transform: "scale(0.42)", margin: "-110px 0 -120px" }} aria-hidden>
          <QorumCouncilOrb
            phase={state.currentPhase === "verdict" ? "converge" : state.currentPhase}
            size={320}
          />
        </div>
        <div
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: "italic",
            fontSize: 16,
            color: "var(--charcoal)",
            marginTop: 6,
          }}
        >
          {activeName ? (
            <>
              <strong style={{ fontWeight: 700 }}>{activeName}</strong>{" "}
              <span style={{ color: "var(--smoke)" }}>rédige…</span>
            </>
          ) : (
            <span style={{ color: "var(--smoke)" }}>Le Conseil se prépare…</span>
          )}
        </div>
      </div>

      {/* Bubbles stream */}
      <div
        ref={streamRef}
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        aria-label="Bulles du Conseil"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "26px 40px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 22,
        }}
      >
        {messages.map((m, i) => {
          const prevRound = i > 0 ? messages[i - 1].round : undefined;
          const showRoundHeader =
            typeof m.round === "number" && m.round !== prevRound;
          return (
            <div key={m.id} style={{ display: "contents" }}>
              {showRoundHeader && (
                <div
                  data-testid={`round-header-${m.round}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    margin: "10px 0 2px",
                    animation: "qorumFadeIn 480ms ease-out",
                  }}
                >
                  <span style={{ flex: 1, height: 1, background: "var(--stone-mid)" }} />
                  <span
                    style={{
                      fontFamily: '"DM Mono", monospace',
                      fontSize: 10,
                      letterSpacing: 2.5,
                      textTransform: "uppercase",
                      color: "var(--gold-deep)",
                    }}
                  >
                    Tour {m.round} de délibération
                  </span>
                  <span style={{ flex: 1, height: 1, background: "var(--stone-mid)" }} />
                </div>
              )}
              <div
                data-testid={`chat-message-${i}`}
                style={{ animation: "qorumBubbleEnter 620ms cubic-bezier(0.16, 1, 0.3, 1) both" }}
              >
                <CouncilBubble bubble={m} side={i % 2 === 0 ? "left" : "right"} />
              </div>
            </div>
          );
        })}
        {showTyping && (
          <div
            data-testid="typing-indicator"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 0 0 58px",
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontSize: 14,
              color: "var(--leather)",
              opacity: 0.85,
            }}
          >
            <span>{nextPendingName} écrit</span>
            <span style={{ display: "inline-flex", gap: 4 }}>
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: "var(--gold-deep)",
                    display: "inline-block",
                    animation: `qorumTypingBounce 1.2s ${i * 0.18}s infinite ease-in-out`,
                  }}
                />
              ))}
              <style>{`@keyframes qorumTypingBounce{0%,80%,100%{transform:translateY(0);opacity:.35}40%{transform:translateY(-3px);opacity:1}}`}</style>
            </span>
          </div>
        )}
        {state.queuedCount > 0 && (
          <div
            data-testid="queue-indicator"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 14,
              padding: "8px 0",
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: "var(--smoke)",
            }}
          >
            <span>{state.queuedCount} {state.queuedCount > 1 ? "prises de parole en attente" : "prise de parole en attente"}</span>
            <button
              type="button"
              onClick={state.accelerate}
              data-testid="accelerate-button"
              aria-label="Accélérer l'affichage des bulles"
              style={{
                background: "var(--charcoal)",
                color: "var(--ivory)",
                border: "none",
                padding: "6px 14px",
                fontFamily: '"DM Mono", monospace',
                fontSize: 10,
                letterSpacing: 2,
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Accélérer
            </button>
          </div>
        )}
        <div ref={bottomRef} aria-hidden style={{ height: 1 }} />
        <style>{`@keyframes qorumFadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}@keyframes qorumBubbleEnter{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}`}</style>
      </div>


      {/* Input bar */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{
          padding: "16px 32px 20px",
          borderTop: "0.5px solid var(--stone-mid)",
          background: "var(--ivory)",
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <label htmlFor="la-table-input" className="sr-only" style={{ position: "absolute", left: -10000, width: 1, height: 1, overflow: "hidden" }}>
          Intervenir dans le débat du Conseil
        </label>
        <input
          id="la-table-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Intervenir dans le débat…"
          aria-label="Intervenir dans le débat du Conseil"
          style={{
            flex: 1,
            border: "0.5px solid var(--stone-mid)",
            background: "var(--cream)",
            padding: "14px 18px",
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: "italic",
            fontSize: 16,
            color: "var(--charcoal)",
            outline: "none",
          }}
        />
        <button
          type="submit"
          aria-label="Envoyer"
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "var(--charcoal)",
            color: "var(--ivory)",
            border: "none",
            cursor: "pointer",
            fontSize: 18,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          →
        </button>
      </form>
    </section>
  );
}

function ActDots({ phase }: { phase: string }) {
  const order = ["summon", "debate", "converge", "verdict"];
  const idx = order.indexOf(phase);
  return (
    <span style={{ display: "inline-flex", gap: 5 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background:
              i <= Math.max(0, idx) ? "var(--leather)" : "var(--stone-mid)",
            display: "inline-block",
          }}
        />
      ))}
    </span>
  );
}

/* ─────────────────────── RIGHT PANEL (380px) ─────────────────────── */

function Panel({ state, archiveMode: _archiveMode = false }: { state: ReturnType<typeof useDeliberation>; archiveMode?: boolean }) {
  return (
    <aside
      data-rp="panel-mobile-full"
      style={{
        background: "var(--ivory)",
        borderLeft: "0.5px solid var(--stone-mid)",
        padding: "26px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 28,
        overflowY: "auto",
      }}
    >
      {/* L'Investigation en direct */}
      <InvestigationLive
        sources={state.investigationSources}
        progress={state.investigationProgress}
        queries={state.researchQueries}
        frozen={state.isCompleted}
      />

      {/* Tour de table */}
      <div>
        <Label>Tour de table</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
          {TURN_ORDER.map((pid, idx) => {
            const c = COUNCIL_BY_ID[pid];
            const b = state.bubbles.find((x) => x.persona === pid);
            const status: "spoke" | "active" | "waiting" =
              b?.state === "complete"
                ? "spoke"
                : b?.state === "typing" || b?.state === "streaming" || idx === state.currentTurn - 1
                ? "active"
                : "waiting";
            return <CouncilorRow key={pid} c={c} status={status} />;
          })}
        </div>
      </div>

      {/* Indice de conviction */}
      <div>
        <Label>Indice de conviction</Label>
        <div
          style={{
            marginTop: 12,
            background: "var(--cream)",
            border: "0.5px solid var(--stone-mid)",
            padding: "18px 18px 16px",
          }}
        >
          {(() => {
            const spoken = state.bubbles.filter((x) => x.state === "complete").length;
            const total = Math.max(1, state.totalTurns || 6);
            // Score progresse de 0.20 → ~0.85 au fil des prises de parole
            const base = 0.2 + (spoken / total) * 0.65;
            const score = Math.min(0.95, base).toFixed(2);
            return (
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <span
                  style={{
                    fontFamily: '"Anton", sans-serif',
                    fontSize: 44,
                    color: "var(--navy)",
                    lineHeight: 1,
                    letterSpacing: -1,
                    fontVariantNumeric: "tabular-nums",
                    transition: "color 400ms ease",
                  }}
                >
                  {score}
                </span>
                <span
                  style={{
                    fontFamily: '"DM Mono", monospace',
                    fontSize: 10,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: "var(--gold-deep)",
                  }}
                >
                  {spoken >= total ? "consolidé" : "en cours"}
                </span>
              </div>
            );
          })()}

          <div
            style={{
              marginTop: 12,
              height: 4,
              background:
                "linear-gradient(to right, var(--terracotta) 0%, var(--accent-cramoisi) 50%, #3D5A40 100%)",
              opacity: 0.85,
            }}
          />
          <p
            style={{
              marginTop: 12,
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontSize: 13.5,
              lineHeight: 1.45,
              color: "var(--smoke)",
            }}
          >
            Pondéré sur {Math.max(1, state.currentTurn)} prises de parole · {state.totalTurns} attendues.
            La convergence se précisera à mesure des actes.
          </p>
        </div>
      </div>

      {/* Actes */}
      <div>
        <Label>Actes</Label>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 6,
          }}
        >
          {ACTES.map((a, i) => {
            // Mapping rounds → actes : round 1 = Acte I, round 2 = Acte II, round 3 = Acte III.
            // `meeting_done` (currentPhase==="verdict") marque tous les actes comme accomplis.
            const isVerdict = state.currentPhase === "verdict";
            const activeIdx = Math.max(0, state.currentTurn - 1);
            const done = isVerdict || i < activeIdx;
            const active = !isVerdict && i === activeIdx;
            return (
              <div
                key={a.key}
                style={{
                  border: "0.5px solid var(--stone-mid)",
                  background: active
                    ? "var(--charcoal)"
                    : done
                      ? "var(--cream)"
                      : "transparent",
                  color: active
                    ? "var(--ivory)"
                    : done
                      ? "var(--charcoal)"
                      : "var(--smoke)",
                  padding: "10px 6px",
                  textAlign: "center",
                  fontFamily: '"DM Mono", monospace',
                  fontSize: 9,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  lineHeight: 1.3,
                }}
              >
                {a.label}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

function CouncilorRow({
  c,
  status,
}: {
  c: { id: string; initial: string; name: string; model: string; color: string };
  status: "spoke" | "active" | "waiting";
}) {
  const label = status === "spoke" ? "✓ Parlé" : status === "active" ? "Rédige" : "En attente";
  const isActive = status === "active";
  const Logo = LLM_MODELS[c.id as LLMPersonaId]?.logo;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        border: isActive ? "1px solid var(--accent-cramoisi)" : "0.5px solid transparent",
        background: isActive ? "var(--cream)" : "transparent",
        opacity: status === "waiting" ? 0.65 : 1,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          background: c.color,
          color: "var(--ivory)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: '"Playfair Display", serif',
          fontWeight: 700,
          fontSize: 16,
          flexShrink: 0,
        }}
      >
        {Logo ? <Logo width={20} height={20} /> : c.initial}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: '"Playfair Display", serif',
            fontStyle: "italic",
            fontWeight: 700,
            fontSize: 16,
            color: "var(--charcoal)",
            lineHeight: 1.1,
          }}
        >
          {c.name}
        </div>
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 9,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            color: "var(--smoke)",
            marginTop: 2,
          }}
        >
          {c.model}
        </div>
      </div>
      <span
        style={{
          fontFamily: '"DM Mono", monospace',
          fontSize: 9,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          color:
            status === "spoke"
              ? "var(--gold-deep)"
              : status === "active"
              ? "var(--leather)"
              : "var(--smoke)",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </div>
  );
}
